import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { FaqEdge, NoticeEdge } from 'types/support';

type Language = 'ko' | 'en' | string;

export type NoticeModalState = {
	close: boolean;
	close_banner: boolean;
};

export type PublicDataState = {
	service_guide_link: string;
	service_introduce_link: string;
	channeltalk: string;
	close_while_a_day: boolean;
	banner_close_forever: boolean;
};

export type SignatureState = {
	exist: boolean;
	image: string;
	process: number;
};

export type SupportState = {
	support_tab: number;
	search_keyword: string | null;
	notice_data: NoticeEdge[] | null;
	notice_id: string | number | null;
	notice_category: number;
	faq_data: FaqEdge[] | null;
	faq_category: number;
	faq_category_list: [string, number][];
};

type PersistedState = {
	publicData: PublicDataState;
	support: SupportState;
};

export type AppStore = PersistedState & {
	noticeModal: NoticeModalState;
	signature: SignatureState;
	setModalClose: (close: boolean) => void;
	setBannerClose: (close: boolean) => void;
	setServiceGuideLink: (link: string) => void;
	setServiceIntroduceLink: (language: Language) => void;
	setChanneltalk: (link: string) => void;
	setCloseWhileADay: (close: boolean) => void;
	setCloseBannerForever: (close: boolean) => void;
	setSignatureExist: (exist: boolean) => void;
	setSignatureImage: (image: string) => void;
	setSignatureProcess: (process: number) => void;
	setSupportTab: (tab: number) => void;
	setKeyword: (keyword: string | null) => void;
	setNoticeData: (data: NoticeEdge[] | null) => void;
	setNoticeId: (id: string | number | null) => void;
	setNoticeCategory: (category: number) => void;
	setFaqData: (data: FaqEdge[] | null) => void;
	setFaqCategory: (category: number) => void;
	setFaqCategoryList: (categoryList: [string, number][]) => void;
};

const INTRODUCTION_PDF_PREFIX: Record<string, string> = {
	ko: 'introduce/DONUE+Service+Introduction.pdf',
	en: 'introduce/DONUE+Service+Introduction_en.pdf'
};

const initialNoticeModal: NoticeModalState = {
	close: false,
	close_banner: false
};

const initialPublicData: PublicDataState = {
	service_guide_link: '',
	service_introduce_link: '',
	channeltalk: '',
	close_while_a_day: false,
	banner_close_forever: false
};

const initialSignature: SignatureState = {
	exist: false,
	image: '',
	process: 0
};

const initialSupport: SupportState = {
	support_tab: 0,
	search_keyword: null,
	notice_data: null,
	notice_id: null,
	notice_category: 0,
	faq_data: null,
	faq_category: 0,
	faq_category_list: [
		['SUPPORT_FAQ_TAB1', 0],
		['SUPPORT_FAQ_TAB2', 0],
		['SUPPORT_FAQ_TAB3', 0],
		['SUPPORT_FAQ_TAB4', 0],
		['SUPPORT_FAQ_TAB5', 0],
		['SUPPORT_FAQ_TAB6', 0],
		['SUPPORT_FAQ_TAB7', 0]
	]
};

const isBrowser = typeof window !== 'undefined';

const readLegacyPersistedSlice = <T>(sliceKey: string, fallback: T): T => {
	if (!isBrowser) return fallback;

	try {
		const persistedRoot = window.localStorage.getItem('persist:root');
		if (!persistedRoot) return fallback;

		const root = JSON.parse(persistedRoot);
		const rawSlice = root?.[sliceKey];
		if (!rawSlice) return fallback;

		return {
			...fallback,
			...JSON.parse(rawSlice)
		};
	} catch {
		return fallback;
	}
};

const createInitialPersistedState = (): PersistedState => ({
	publicData: initialPublicData,
	support: initialSupport
});

export const useAppStore = create<AppStore>()(
	persist(
		(set) => ({
			...createInitialPersistedState(),
			noticeModal: initialNoticeModal,
			signature: initialSignature,
			setModalClose: (close) => set((state) => ({ noticeModal: { ...state.noticeModal, close } })),
			setBannerClose: (close_banner) => set((state) => ({ noticeModal: { ...state.noticeModal, close_banner } })),
			setServiceGuideLink: (service_guide_link) => set((state) => ({ publicData: { ...state.publicData, service_guide_link } })),
			setServiceIntroduceLink: (language) =>
				set((state) => {
					const introductionPdfPrefix = INTRODUCTION_PDF_PREFIX[language] ?? INTRODUCTION_PDF_PREFIX.ko;

					return {
						publicData: {
							...state.publicData,
							service_introduce_link: `${process.env.GATSBY_PUBLIC_STATIC_URL}/${introductionPdfPrefix}`
						}
					};
				}),
			setChanneltalk: (channeltalk) => set((state) => ({ publicData: { ...state.publicData, channeltalk } })),
			setCloseWhileADay: (close_while_a_day) => set((state) => ({ publicData: { ...state.publicData, close_while_a_day } })),
			setCloseBannerForever: (banner_close_forever) =>
				set((state) => ({ publicData: { ...state.publicData, banner_close_forever } })),
			setSignatureExist: (exist) => set((state) => ({ signature: { ...state.signature, exist } })),
			setSignatureImage: (image) => set((state) => ({ signature: { ...state.signature, image } })),
			setSignatureProcess: (process) => set((state) => ({ signature: { ...state.signature, process } })),
			setSupportTab: (support_tab) => set((state) => ({ support: { ...state.support, support_tab } })),
			setKeyword: (search_keyword) => set((state) => ({ support: { ...state.support, search_keyword } })),
			setNoticeData: (notice_data) => set((state) => ({ support: { ...state.support, notice_data } })),
			setNoticeId: (notice_id) => set((state) => ({ support: { ...state.support, notice_id } })),
			setNoticeCategory: (notice_category) => set((state) => ({ support: { ...state.support, notice_category } })),
			setFaqData: (faq_data) => set((state) => ({ support: { ...state.support, faq_data } })),
			setFaqCategory: (faq_category) => set((state) => ({ support: { ...state.support, faq_category } })),
			setFaqCategoryList: (faq_category_list) => set((state) => ({ support: { ...state.support, faq_category_list } }))
		}),
		{
			name: 'donue-zustand-root',
			storage: createJSONStorage(() => localStorage),
			// Read storage only after React has hydrated the server HTML.
			skipHydration: true,
			partialize: (state) => ({
				publicData: state.publicData,
				support: state.support
			})
		}
	)
);

export const hydrateAppStore = () => {
	if (!isBrowser) return;

	if (!window.localStorage.getItem('donue-zustand-root')) {
		useAppStore.setState(createInitialPersistedState());
		useAppStore.setState({
			publicData: readLegacyPersistedSlice('publicData', initialPublicData),
			support: readLegacyPersistedSlice('support', initialSupport)
		});
	}

	return useAppStore.persist.rehydrate();
};
